import { useEffect, useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, eq, modulePresetsCollection, storesCollection } from "@cliqsuite/db";

import { useAuth } from "../../lib/auth";
import { BusinessTypePicker } from "./BusinessTypePicker";
import { AuthFrame, AuthHead, IconField } from "./AuthFrame";
import { CurrencySelect } from "../common/CurrencySelect";


/**
 * Every business needs at least one store before POS, devices, modules or
 * anything else scoped to a store can work. Owner signup creates a first
 * store together with the business (see /auth/owner-signup), so this is a
 * fail-safe, not the primary onboarding path — it only ever shows if a
 * business somehow ends up with zero stores (e.g. its only store gets
 * deleted later), and blocks the rest of the app until one exists again.
 */
export function FirstStoreGate({ businessId, businessName }: { businessId: string; businessName?: string }) {
  const { logout } = useAuth();
  const { data: businessRows } = useLiveQuery(
    (q) => q.from({ businesses: businessesCollection }).where(({ businesses }) => eq(businesses.id, businessId)),
    [businessId],
  );
  const business = businessRows?.[0];
  const { data: presetRows } = useLiveQuery((q) => q.from({ p: modulePresetsCollection }));

  const [name, setName] = useState(businessName ?? "");
  const [address, setAddress] = useState("");
  const [currency, setCurrency] = useState("MMK");
  const [currencyPrecision, setCurrencyPrecision] = useState<0 | 2 | 3>(0);
  const [businessType, setBusinessType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pre-select whatever business_type the row already has (it always has
  // some value — 'restaurant' is the column default) so this doesn't feel
  // like starting from scratch for an account that already picked one at
  // signup. Still always shown and changeable — mandatory, not skippable,
  // since installed_modules being non-empty doesn't mean a type was ever
  // deliberately chosen (e.g. modules toggled manually before this existed).
  useEffect(() => {
    if (business && !businessType) setBusinessType(business.business_type);
  }, [business, businessType]);

  const create = async () => {
    if (!name.trim()) {
      setError("Store name is required.");
      return;
    }
    if (!businessType) {
      setError("Pick what your business does — this recommends the right modules.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const tx = storesCollection.insert({
        id: crypto.randomUUID(),
        business_id: businessId,
        name: name.trim(),
        address: address || null,
        city: null,
        phone: null,
        email: null,
        latitude: null,
        longitude: null,
        geofence_radius_m: null,
        store_icon: "storefront",
        business_hours: {},
        currency_code: currency,
        tax_rate: "0.05",
        tax_label: "Tax",
        currency_precision: currencyPrecision,
        guest_portal_enabled: false,
        is_active: true,
        sort_order: 0,
        settings: {},
        created_at: now,
        updated_at: now,
        deleted_at: null,
      });
      const preset = (presetRows ?? []).find((p) => p.key === businessType);
      if (business && preset) {
        businessesCollection.update(businessId, (draft) => {
          draft.business_type = businessType as typeof draft.business_type;
          draft.installed_modules = [...preset.modules];
        });
      }
      await tx.isPersisted.promise;
    } catch {
      setError("Couldn't create the store — please try again.");
      setSubmitting(false);
    }
  };

  return (
    <AuthFrame wide>
      <form
        className="o-auth__form"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <AuthHead icon="storefront" title="Add your first store" sub="Registers, staff and stock all belong to a store." />
        <IconField label="Store name" icon="storefront" value={name} onChange={(e) => setName(e.target.value)} placeholder="Downtown" autoFocus />
        <IconField label="Address (optional)" icon="location_on" value={address} onChange={(e) => setAddress(e.target.value)} />
        <div className="o-ifield">
          <span className="o-field__label">Currency</span>
          <CurrencySelect value={currency} onChange={(code, precision) => { setCurrency(code); setCurrencyPrecision(precision); }} />
        </div>
        <div className="o-field__label">What do you run?</div>
        <BusinessTypePicker value={businessType} onChange={setBusinessType} />
        {error && <div className="o-error" role="alert" style={{ marginTop: 0 }}>{error}</div>}
        <button type="submit" className="o-auth__submit" disabled={submitting}>
          {submitting ? "Adding…" : "Add store"}
          {!submitting && <span className="ms" aria-hidden="true">arrow_forward</span>}
        </button>
        <button type="button" className="o-link o-auth__alt" onClick={logout}>
          Sign out
        </button>
      </form>
    </AuthFrame>
  );
}
